import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { cloneDeep, forEach, isEmpty, filter, assign } from 'lodash';
import { Row, Col, Modal } from 'react-bootstrap';
import { Bert } from 'meteor/themeteorchef:bert';
import turf from '@turf/turf';

import * as projectOps from '../../../api/Projects/project-operations';
import { FeatureCollectionPoints } from '../../../api/SchemaUtilities/GeoJSONSchema';
import * as videoOps from '../../../api/Videos/video-operations';
import trackFeaturePointPropertiesSchema from '../../../api/Videos/VideoCoordinatesSchema';
import {
  densifyLine,
  adaptLineStringTimes,
  syncLineAtControlPoint
} from '../../../modules/sync-video-line';

import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';

import ColButton from '../../atoms/ColButton';
import SvgMilestones from '../../components/SvgMilestones';
import VideoEditor from '../../components/VideoEditor';
import VideoSynchronizer from '../../components/VideoSynchronizer';
import Loading from '../../components/Loading';
import AnnotationTemplatesContainer from '../AnnotationTemplatesContainer';
import VideoEditorBaseMaps from '../../components/VideoEditorBaseMaps';

const STEP_NAME_MARGIN = 34; // pixels
const SMALL_WINDOW_WIDTH_RELATION = 2 / 3;
const EXTRA_SMALL_WINDOW_WIDTH_RELATION = 1;
const SMALL_SCREEN_SIZE = 576; // pixels
const MAX_NUMBER_OF_POINTS = 2400;

const PreviousStepButton = ({ editionStep, handlePreviousStepChange }) => {
  if (editionStep === 0) return <Col sm={2} />;
  return <ColButton sm={2} onClick={handlePreviousStepChange} type="button" spanLabel="Previous" />;
};

PreviousStepButton.propTypes = {
  editionStep: PropTypes.number.isRequired,
  handlePreviousStepChange: PropTypes.func.isRequired
};

const NextStepButton = ({ editionStep, handleNextStepChange, handleSubmit }) => {
  const variant = editionStep === 3 ? 'success' : 'primary';
  const buttonType = editionStep === 0 ? 'submit' : 'button';
  const onClickFunction = editionStep === 3 ? handleSubmit : handleNextStepChange;
  const spanLabel = editionStep === 3 ? 'Save Changes' : 'Next';

  return (
    <ColButton
      sm={2}
      variant={variant}
      onClick={onClickFunction}
      type={buttonType}
      form="videoEditorForm"
      spanLabel={spanLabel}
    />
  );
};

NextStepButton.propTypes = {
  editionStep: PropTypes.number.isRequired,
  handleNextStepChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired
};

const Stepper = ({
  editionStep,
  milestonesWidth,
  multiStepItems,
  handleNextStepChange,
  handlePreviousStepChange,
  handleSubmit
}) => {
  return (
    <Row noGutters>
      <PreviousStepButton
        editionStep={editionStep}
        handlePreviousStepChange={handlePreviousStepChange}
      />
      <Col sm={8}>
        <SvgMilestones
          height={50}
          width={milestonesWidth}
          elements={multiStepItems}
          lineWidthRatio={0.06}
          horizontal
        />
      </Col>
      <NextStepButton
        editionStep={editionStep}
        handleNextStepChange={handleNextStepChange}
        handleSubmit={handleSubmit}
      />
    </Row>
  );
};

Stepper.propTypes = {
  editionStep: PropTypes.number.isRequired,
  milestonesWidth: PropTypes.number.isRequired,
  multiStepItems: PropTypes.array.isRequired,
  handleNextStepChange: PropTypes.func.isRequired,
  handlePreviousStepChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired
};

class VideoEditorContainer extends Component {
  state = {
    updatedVideo: {
      name: videoOps.getName(this.props.video),
      description: videoOps.getDescription(this.props.video),
      url: videoOps.getURL(this.props.video),
      geometryFileName: videoOps.getGeometryFileName(this.props.video),
      logFileName: videoOps.getLogFileName(this.props.video),
      latLng0: videoOps.getLatLng0(this.props.video),
      type: videoOps.getType(this.props.video),
      noCoordinates: videoOps.areCoordinatesUndefined(this.props.video),
      tags: videoOps.getTags(this.props.video),
      track: videoOps.getTrack(this.props.video),
      controlPoints: videoOps.getControlPoints(this.props.video),
      coordinatesDataType: videoOps.getCoordinatesDataType(this.props.video),
      annotationTemplates: videoOps.getAnnotationTemplates(this.props.video),
      timeGPSRecording: videoOps.getTimeGPS(this.props.video),
      geomotionMap: videoOps.getGeomotionMap(this.props.video)
    },
    editionStep: 0,
    featureArray: [],
    ignoreCSVTimes: false,
    isDataLoaded: false,
    isTrackUpdated: false,
    milestonesWidth: 300,
    showModal: false,
    suggestions: [],
    videoDuration: videoOps.getDefaultDurationVideo(this.props.video)
  };

  async componentDidMount() {
    const { video } = this.props;
    const { isDataLoaded } = this.state;

    const existingVideo = !isEmpty(videoOps.getId(video));
    if (existingVideo && !isDataLoaded) this.loadDataFile();

    this.setMilestonesDimensions();
    this.updateSuggestions();
    window.addEventListener('resize', this.setMilestonesDimensions);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.setMilestonesDimensions);
  }

  setMilestonesDimensions = () =>
    this.setState({
      milestonesWidth: Math.round(
        (window.innerWidth - STEP_NAME_MARGIN) *
          (window.innerWidth < SMALL_SCREEN_SIZE
            ? EXTRA_SMALL_WINDOW_WIDTH_RELATION
            : SMALL_WINDOW_WIDTH_RELATION)
      )
    });

  updateVideo = newVideo =>
    this.setState(prevState => {
      const newUpdatedVideo = cloneDeep(prevState.updatedVideo);
      forEach(newVideo, (value, key) => {
        newUpdatedVideo[key] = value;
      });
      return { updatedVideo: newUpdatedVideo };
    });

  updateVideoProperty = (propertyName, newValue) =>
    this.setState(prevState => {
      const newUpdatedVideo = { ...prevState.updatedVideo };
      newUpdatedVideo[propertyName] = newValue;
      return { updatedVideo: newUpdatedVideo };
    });

  changeVideoEditorContainerStateProperty = (property, newValue) =>
    this.setState({ [property]: newValue });

  handleNextStepChange = () => {
    const { updatedVideo, editionStep } = this.state;
    const stepToChange =
      videoOps.areCoordinatesFromPoint(updatedVideo) && editionStep === 1 ? 2 : 1;
    if (editionStep > 0) this.setState({ editionStep: editionStep + stepToChange });
  };

  handlePreviousStepChange = () => {
    const { updatedVideo, editionStep } = this.state;
    const stepToChange =
      videoOps.areCoordinatesFromPoint(updatedVideo) && editionStep === 3 ? 2 : 1;
    this.setState({ editionStep: editionStep - stepToChange });
  };

  handleSubmit = async () => {
    const { video, match } = this.props;
    const { featureArray, updatedVideo } = this.state;

    const existingVideo = !isEmpty(videoOps.getId(video));
    const { project_id: project } = match.params;

    this.setState({ showModal: true });

    const defaultFeatureArray = [
      turf.point(videoOps.getLatLng0(updatedVideo), {
        distanceTravelled: 0,
        isControlPoint: false,
        timeUS: 0
      })
    ];
    const featureArrayToUse = videoOps.areCoordinatesFromPoint(updatedVideo)
      ? defaultFeatureArray
      : featureArray;

    const densePointFeatureCollection = turf.featureCollection(featureArrayToUse);

    const methodToCall = existingVideo ? 'videos.update' : 'videos.insert';

    // FIXME: se queda muñeco durante la validación

    try {
      FeatureCollectionPoints.validate(densePointFeatureCollection);

      forEach(featureArray, feature => {
        trackFeaturePointPropertiesSchema.validate(feature.properties);
      });

      const videoToSave = {
        project,
        name: videoOps.getName(updatedVideo),
        description: videoOps.getDescription(updatedVideo),
        url: videoOps.getURL(updatedVideo),
        geometryFileName: videoOps.getGeometryFileName(updatedVideo),
        logFileName: videoOps.getLogFileName(updatedVideo),
        noCoordinates: videoOps.areCoordinatesUndefined(updatedVideo),
        latLng0: videoOps.getLatLng0(updatedVideo),
        type: videoOps.getType(updatedVideo),
        tags: videoOps.getTags(updatedVideo),
        track: videoOps.getTrack(updatedVideo),
        controlPoints: videoOps.getControlPoints(updatedVideo),
        coordinatesDataType: videoOps.getCoordinatesDataType(updatedVideo),
        annotationTemplates: videoOps.getAnnotationTemplates(updatedVideo),
        densePointFeatureCollection,
        timeGPSRecording: videoOps.getTimeGPS(updatedVideo)
      };
      if (!isEmpty(videoOps.getGeomotionMap(updatedVideo))) {
        assign(videoToSave, {
          geomotionMap: videoOps.getGeomotionMap(updatedVideo)
        });
      }

      if (existingVideo) videoToSave._id = videoOps.getId(video);

      const videoId = await methodPromise(methodToCall, videoToSave);

      this.setState({ showModal: false });
      this.redirectToVideoView(videoId);
    } catch (error) {
      this.setState({ showModal: false });
      handleErrorInClient(error);
    }
  };

  redirectToVideoView(videoId) {
    const { history, match } = this.props;
    const { project_id: projectId } = match.params;
    Bert.alert('Video saved!', 'success');
    history.push(`/projects/${projectId}/videos/${videoId}`);
  }

  async updateSuggestions() {
    const newSuggestions = await methodPromise('videos.getTagSuggestions');
    this.setState({ suggestions: newSuggestions });
  }

  loadDataFile() {
    const refreshRate = 1000;
    const { videoDuration, updatedVideo } = this.state;
    let newAllFeature;
    let featureArraySync;

    let newRefreshRate = refreshRate;
    const pointsInTrack = videoDuration / (refreshRate * 1000);
    if (pointsInTrack > MAX_NUMBER_OF_POINTS) {
      newRefreshRate *= pointsInTrack / MAX_NUMBER_OF_POINTS;
    }

    if (updatedVideo.coordinatesDataType === 'point') {
      featureArraySync = [
        turf.point(videoOps.getLatLng0(updatedVideo), {
          distanceTravelled: 0,
          isControlPoint: false,
          timeUS: 0
        })
      ];
      if (!isEmpty(updatedVideo.controlPoints)) {
        featureArraySync = featureArraySync.concat(updatedVideo.controlPoints.features);
      }
    } else {
      const controlPointsVideo = videoOps.getControlPoints(updatedVideo);

      const newFeatureArray = adaptLineStringTimes({
        line: videoOps.getTrack(updatedVideo),
        totalTime: videoDuration
      });

      newAllFeature = densifyLine({
        featureArray: newFeatureArray,
        refreshRate: newRefreshRate
      });

      if (
        controlPointsVideo.features.length > 2 &&
        updatedVideo.coordinatesDataType !== 'csvGoPro'
      ) {
        const filterControlPoints = filter(
          controlPointsVideo.features,
          (element, index, array) => index !== 0 && index !== array.length - 1
        );
        const lineSyncByControlPoint = filterControlPoints.map(element => {
          const syncLine = syncLineAtControlPoint({
            featureArray: newAllFeature,
            latLngPoint: {
              lng: element.geometry.coordinates[0],
              lat: element.geometry.coordinates[1]
            },
            videoTime: element.properties.timeUS
          });
          newAllFeature = syncLine.updatedFeatureArray;
          return newAllFeature;
        });

        featureArraySync = lineSyncByControlPoint[lineSyncByControlPoint.length - 1];
      } else {
        featureArraySync = newAllFeature;
      }
    }

    const videoData = {
      type: 'FeatureCollection',
      features: featureArraySync
    };

    this.setState({ featureArray: videoData.features, isDataLoaded: true });
  }

  render() {
    const { video, history, match, project, ...rest } = this.props;
    const {
      updatedVideo,
      editionStep,
      featureArray,
      ignoreCSVTimes,
      isDataLoaded,
      isTrackUpdated,
      milestonesWidth,
      showModal,
      suggestions
    } = this.state;

    const existingVideo = !isEmpty(videoOps.getId(video));

    const multiStepItems = [
      {
        text: 'Data Definition',
        selected: editionStep >= 0,
        checked: editionStep > 0,
        callback: () => {}
      },
      {
        text: 'Map Base Selection',
        selected: editionStep >= 1,
        checked: editionStep > 1,
        callback: () => {}
      },
      {
        text: 'Template Selection',
        selected: editionStep >= 3,
        checked: editionStep > 3,
        callback: () => {}
      }
    ];
    if (!videoOps.areCoordinatesFromPoint(updatedVideo))
      multiStepItems.splice(2, 0, {
        text: 'Synchronization',
        selected: editionStep >= 2,
        checked: editionStep > 2,
        callback: () => {}
      });

    let mapLocation = projectOps.getMapLocation(project);
    if (existingVideo) mapLocation = turf.point(videoOps.getLatLng0(updatedVideo), { zoom: 15 });

    if (existingVideo && !isDataLoaded) return <Loading />;
    return (
      <>
        <Modal show={showModal}>
          <Modal.Body>Saving video. Please, wait.</Modal.Body>
        </Modal>
        <Row noGutters>
          <Col xs={12} className="pb-1">
            <Stepper
              editionStep={editionStep}
              milestonesWidth={milestonesWidth}
              multiStepItems={multiStepItems}
              handleNextStepChange={this.handleNextStepChange}
              handlePreviousStepChange={this.handlePreviousStepChange}
              handleSubmit={this.handleSubmit}
            />
          </Col>
          <Col xs={12} className="p-0 w-100">
            {editionStep === 0 && (
              <VideoEditor
                featureArray={featureArray}
                ignoreCSVTimes={ignoreCSVTimes}
                isTrackUpdated={isTrackUpdated}
                mapLocation={mapLocation}
                suggestions={suggestions}
                updatedVideo={updatedVideo}
                changeVideoEditorContainerStateProperty={
                  this.changeVideoEditorContainerStateProperty
                }
                updateVideo={this.updateVideo}
                {...rest}
              />
            )}
            {editionStep === 1 && (
              <VideoEditorBaseMaps
                mapLocation={mapLocation}
                deployEnv="gmv"
                updatedVideo={updatedVideo}
                updateVideoProperty={this.updateVideoProperty}
                {...rest}
              />
            )}
            {editionStep === 2 && (
              <VideoSynchronizer
                featureArray={featureArray}
                history={history}
                ignoreCSVTimes={ignoreCSVTimes}
                isTrackUpdated={isTrackUpdated}
                match={match}
                project={project}
                updatedVideo={updatedVideo}
                video={video}
                changeVideoEditorContainerStateProperty={
                  this.changeVideoEditorContainerStateProperty
                }
                updateVideoProperty={this.updateVideoProperty}
                {...rest}
              />
            )}

            {editionStep === 3 && (
              <AnnotationTemplatesContainer
                history={history}
                isVideoEditor
                match={match}
                updatedVideo={updatedVideo}
                changeVideoEditorContainerStateProperty={
                  this.changeVideoEditorContainerStateProperty
                }
                updateVideoProperty={this.updateVideoProperty}
              />
            )}
          </Col>
        </Row>
      </>
    );
  }
}

VideoEditorContainer.defaultProps = {
  video: {}
};

VideoEditorContainer.propTypes = {
  video: PropTypes.object,
  project: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired
};

export default VideoEditorContainer;
